/**
 * Group-transfer state eviction — called when a peer detaches from the
 * lifecycle store (PEER-RECOVERY, ANTIGLARE rollback, EMAIL_MIGRATE,
 * connection-retry-stale, connection-dead, file-channel close, etc.).
 *
 * Why this exists:
 *   Four node-level maps track in-flight group-transfer state keyed by
 *   peer address:
 *     1. `activeGroupServes`         — abort signals per {addr}:{chunkIndex}
 *     2. `activeGroupServeRefCount`  — per-requester serve-seat refcount
 *     3. `groupPullInflight`         — inflight chunk requests per session,
 *                                       each pointing at a source address
 *     4. `groupSeederDirectory`      — known seeders per session
 *
 *   Prior to this helper, cleanup existed only at the natural completion
 *   paths (serve finally block, ACK/epoch abort, transfer completion).
 *   When a peer was *replaced* at the same address (the W10 Edge
 *   PEER-RECOVERY trace from 5/3), the serve refcount stayed at {deadAddr:
 *   N} forever. The fresh peer's GROUP_CHUNK_REQUEST hit the seat-budget
 *   check (`currentServes >= MAX_CONCURRENT_SERVES_PER_PEER`) and got a
 *   silent `return false`. Requesters waited `GROUP_PULL_STALE_MS` (~12s)
 *   per retry, cascading into the "seat assignment failures for later
 *   peers" symptom the user reported for large group file transfers.
 *
 *   The lifecycle store already emits `peer:lifecycle-end` on every
 *   detach path. This function is called from a single subscriber in
 *   `MobyNode` so every detach site gets cleanup for free — matching the
 *   design intent cited in `peer-state-store.ts:9`
 *   ("groups star meshing, multisource chunk routing").
 *
 * Idempotent:
 *   All operations are unconditional deletes or abort-flag sets. Calling
 *   with an unknown address is a no-op. Safe to call multiple times from
 *   different triggers (e.g. both the file-channel `close` handler and
 *   the lifecycle-end subscriber may fire for the same peer — the second
 *   call finds empty maps and returns an empty set).
 *
 * Pure:
 *   Operates only on the passed-in state object. No side effects on
 *   globals, no console output, no event emission. The `MobyNode` wrapper
 *   adds logging and refill dispatch; this layer is unit-testable in
 *   isolation without a full node instance.
 */

/**
 * Narrow structural type for the four maps this function mutates. Matches
 * the public fields on `MobyNode` but declared here so the pure function
 * doesn't pull in the entire `provider.tsx` type graph.
 */
export interface GroupTransferEvictionState {
  activeGroupServes: Map<string, { aborted: boolean; nonce: number }>;
  activeGroupServeRefCount: Map<string, number>;
  groupPullInflight: Map<
    string,
    Map<number, { source: string; [k: string]: unknown }>
  >;
  groupSeederDirectory: Map<string, Map<string, unknown>>;
}

/**
 * Purge every trace of `address` from the four group-transfer maps.
 *
 * @param address  Peer address to purge (hex string).
 * @param state    The four node-level maps to mutate.
 * @returns        Set of session IDs whose `groupPullInflight` lost
 *                 entries — callers may use this to dispatch pull-window
 *                 refill for affected transfers. Size 0 means nothing
 *                 needed eviction.
 */
export function purgeGroupTransferStateForPeer(
  address: string,
  state: GroupTransferEvictionState,
): Set<string> {
  const impactedSessions = new Set<string>();

  // 1. activeGroupServes — keys are `${requesterAddr}:${chunkIndex}`.
  //    Flip aborted=true first so any still-running send loop inside
  //    serveGroupChunkToPeer breaks at its next per-symbol abort check
  //    (provider.tsx:~21342). Then drop the entry so future epoch-retry
  //    lookups at `GROUP_CHUNK_REQUEST` handler (:~18488) don't see stale
  //    signals.
  const servePrefix = `${address}:`;
  for (const key of Array.from(state.activeGroupServes.keys())) {
    if (key.startsWith(servePrefix)) {
      const signal = state.activeGroupServes.get(key);
      if (signal) signal.aborted = true;
      state.activeGroupServes.delete(key);
    }
  }

  // 2. activeGroupServeRefCount — single key per requester address.
  //    This is the map whose stale entries caused the seat-starvation
  //    bug: `isNewTarget = !refCount.has(addr)` returned false after
  //    peer replacement, and the subsequent `currentServes >= MAX` check
  //    rejected every fresh request.
  state.activeGroupServeRefCount.delete(address);

  // 3. groupPullInflight — evict inflight chunks sourced from this peer.
  //    Collect session IDs so the caller can refill pull windows.
  //    Without refill, sessions recover naturally via the MULTISOURCE
  //    sweep (~8s latency) but proactive refill cuts the recovery gap
  //    to microseconds.
  for (const [sessionId, inflight] of state.groupPullInflight.entries()) {
    let evicted = 0;
    for (const [chunkIdx, info] of Array.from(inflight.entries())) {
      if (info.source === address) {
        inflight.delete(chunkIdx);
        evicted++;
      }
    }
    if (evicted > 0) {
      impactedSessions.add(sessionId);
    }
  }

  // 4. groupSeederDirectory — remove this peer from every session's
  //    seeder map. Stale seeder entries corrupted the C5B stagger math
  //    (receiverCount inflated by dead peers → wrong stagger offset)
  //    and inflated the source-selection candidate list.
  for (const seeders of state.groupSeederDirectory.values()) {
    seeders.delete(address);
  }

  return impactedSessions;
}
